
import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Button } from '../components/shared/Button';
import {
   ArrowLeft,
   ChevronDown,
   Brain,
   GitBranch,
   Eye,
   Wand2,
   Settings,
   Zap,
   Shield
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES, EXTERNAL_LINKS } from '../constants';
import { FreeTrialButton } from '../components/shared/FreeTrialButton';

export const AiAutomationPage: React.FC = () => {
   const { t } = useLanguage();
   const [expandedSkill, setExpandedSkill] = useState<number | null>(null);

   const skills = [
      { id: 1, icon: Settings, color: 'green' },
      { id: 2, icon: Zap, color: 'orange' },
      { id: 3, icon: Brain, color: 'blue' },
      { id: 4, icon: GitBranch, color: 'purple' },
      { id: 5, icon: Eye, color: 'cyan' },
      { id: 6, icon: Wand2, color: 'pink' }
   ];

   const getColorClasses = (color: string) => {
      const colors: Record<string, { bg: string; border: string; text: string }> = {
         blue: { bg: 'bg-blue-900/20', border: 'border-blue-500/50', text: 'text-blue-400' },
         purple: { bg: 'bg-purple-900/20', border: 'border-purple-500/50', text: 'text-purple-400' },
         cyan: { bg: 'bg-cyan-900/20', border: 'border-cyan-500/50', text: 'text-cyan-400' },
         pink: { bg: 'bg-pink-900/20', border: 'border-pink-500/50', text: 'text-pink-400' },
         green: { bg: 'bg-green-900/20', border: 'border-green-500/50', text: 'text-green-400' },
         orange: { bg: 'bg-orange-900/20', border: 'border-orange-500/50', text: 'text-orange-400' }
      };
      return colors[color] || colors.blue;
   };

   return (
      <div className="bg-[#050508] text-white min-h-screen pt-32 pb-20 relative">

         {/* Background Effects */}
         <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-blue-600/5 blur-[120px] rounded-full pointer-events-none"></div>

         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

            {/* Back Link */}
            <div className="mb-12">
               <Link to={ROUTES.HOME} className="flex items-center text-gray-400 hover:text-white transition-colors">
                  <ArrowLeft size={20} className="mr-2" />
                  {t('nav.home')}
               </Link>
            </div>

            <AnimatedSection className="text-center mb-32">
               <div className="flex justify-center mb-6">
                  <span className="bg-green-900/20 text-green-400 border border-green-500/30 text-[10px] sm:text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest max-w-[90vw] text-center leading-tight">
                     ✓ SERVIZI OPERATIVI
                  </span>
               </div>

               <h1 className="text-3xl sm:text-5xl md:text-7xl font-black uppercase tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-white to-purple-400">
                  {t('aiAutomation.title')}
               </h1>

               <p className="text-2xl font-light text-gray-300 mb-8 max-w-2xl mx-auto">
                  {t('aiAutomation.subtitle')}
               </p>

               <p className="text-gray-400 max-w-3xl mx-auto leading-relaxed text-lg mb-12">
                  {t('aiAutomation.description')}
               </p>

               <div className="relative z-[100] overflow-visible">
                  <FreeTrialButton
                     serviceName="AI & Automation"
                     buttonText="Contattaci"
                     buttonClassName="bg-white text-black hover:bg-gray-200 border-0 px-8 py-4 font-bold rounded-full shadow-[0_0_20px_rgba(255,255,255,0.3)] inline-flex items-center gap-2"
                  />
               </div>
            </AnimatedSection>

            {/* Feature Teasers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
               <AnimatedSection delay={100}>
                  <div className="bg-[#0B1120] border border-gray-800 p-8 rounded-2xl h-full hover:border-green-500/50 transition-colors group">
                     <div className="w-14 h-14 bg-green-900/20 rounded-xl flex items-center justify-center text-green-400 mb-6 group-hover:scale-110 transition-transform">
                        <Settings size={28} />
                     </div>
                     <h3 className="text-xl font-bold text-white mb-3">{t('aiAutomation.features.title1')}</h3>
                     <p className="text-gray-500 text-sm leading-relaxed">{t('aiAutomation.features.desc1')}</p>
                  </div>
               </AnimatedSection>

               <AnimatedSection delay={200}>
                  <div className="bg-[#0B1120] border border-gray-800 p-8 rounded-2xl h-full hover:border-orange-500/50 transition-colors group">
                     <div className="w-14 h-14 bg-orange-900/20 rounded-xl flex items-center justify-center text-orange-400 mb-6 group-hover:scale-110 transition-transform">
                        <Zap size={28} />
                     </div>
                     <h3 className="text-xl font-bold text-white mb-3">{t('aiAutomation.features.title2')}</h3>
                     <p className="text-gray-500 text-sm leading-relaxed">{t('aiAutomation.features.desc2')}</p>
                  </div>
               </AnimatedSection>

               <AnimatedSection delay={300}>
                  <div className="bg-[#0B1120] border border-gray-800 p-8 rounded-2xl h-full hover:border-blue-500/50 transition-colors group">
                     <div className="w-14 h-14 bg-blue-900/20 rounded-xl flex items-center justify-center text-blue-400 mb-6 group-hover:scale-110 transition-transform">
                        <Shield size={28} />
                     </div>
                     <h3 className="text-xl font-bold text-white mb-3">{t('aiAutomation.features.title3')}</h3>
                     <p className="text-gray-500 text-sm leading-relaxed">{t('aiAutomation.features.desc3')}</p>
                  </div>
               </AnimatedSection>
            </div>

            {/* Skills Section */}
            <AnimatedSection delay={400} className="mb-20">
               <div className="text-center mb-12">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
                     {t('aiAutomation.skills.sectionTitle')}
                  </h2>
                  <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                     {t('aiAutomation.skills.sectionSubtitle')}
                  </p>
               </div>

               <div className="max-w-4xl mx-auto space-y-4">
                  {skills.map((skill, index) => {
                     const Icon = skill.icon;
                     const colors = getColorClasses(skill.color);
                     const isExpanded = expandedSkill === skill.id;

                     return (
                        <div
                           key={skill.id}
                           className={`bg-[#0B1120] border ${isExpanded ? colors.border : 'border-gray-800'} rounded-2xl overflow-hidden transition-all duration-300`}
                        >
                           <button
                              onClick={() => setExpandedSkill(isExpanded ? null : skill.id)}
                              className="w-full p-6 flex items-center justify-between hover:bg-gray-900/30 transition-colors group"
                           >
                              <div className="flex items-center gap-4">
                                 <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center ${colors.text} group-hover:scale-110 transition-transform`}>
                                    <Icon size={24} />
                                 </div>
                                 <h3 className="text-lg sm:text-xl font-bold text-white text-left">
                                    {t(`aiAutomation.skills.skill${skill.id}`)}
                                 </h3>
                              </div>
                              <ChevronDown
                                 size={24}
                                 className={`text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                              />
                           </button>

                           {/* Expanded Content */}
                           <div
                              className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                 }`}
                           >
                              <div className="px-6 pb-6 pt-2">
                                 <p className="text-gray-400 text-sm leading-relaxed">
                                    {t(`aiAutomation.skills.skill${skill.id}Desc`)}
                                 </p>
                              </div>
                           </div>
                        </div>
                     );
                  })}
               </div>
            </AnimatedSection>

         </div>
      </div>
   );
};
